import type { HealthPayload } from "./types";

/**
 * Module-level bridge decoupling the API-liveness detector (`useApiHealthStatus`)
 * from the socket that carries the health heartbeat.
 *
 * The health heartbeat now rides the notifications SSE (one app-shell socket for
 * both), which `useNotifications` owns. It forwards each `health` frame and its
 * connection open/close here; `useApiHealthStatus` subscribes and runs its
 * heartbeat-timeout + hysteresis banner logic as a pure observer — it no longer
 * owns an EventSource, and reconnection is delegated entirely to notifications.
 */
type HealthFrameListener = (payload: HealthPayload) => void;
type ConnectionStateListener = (connected: boolean) => void;

const healthFrameListeners = new Set<HealthFrameListener>();
const connectionStateListeners = new Set<ConnectionStateListener>();

// Last known connection state, replayed to late subscribers so the detector
// learns the socket is already open even if it subscribed after the `open` event
// (the two hooks mount together, but ordering isn't guaranteed).
let lastConnectionState: boolean | null = null;

/** Notifications SSE → a `health` heartbeat frame arrived. */
export const emitHealthFrame = (payload: HealthPayload): void => {
  for (const listener of healthFrameListeners) listener(payload);
};

/** Notifications SSE → its connection opened (`true`) or dropped (`false`). */
export const emitHealthConnectionState = (connected: boolean): void => {
  lastConnectionState = connected;
  for (const listener of connectionStateListeners) listener(connected);
};

export const subscribeToHealthFrames = (listener: HealthFrameListener): (() => void) => {
  healthFrameListeners.add(listener);
  return () => {
    healthFrameListeners.delete(listener);
  };
};

export const subscribeToHealthConnectionState = (listener: ConnectionStateListener): (() => void) => {
  connectionStateListeners.add(listener);
  if (lastConnectionState !== null) listener(lastConnectionState);
  return () => {
    connectionStateListeners.delete(listener);
  };
};
